if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.oldSkuField = this.form.querySelector('#old-sku-prop');
        
        // If old SKU field not found, try alternative selectors
        if (!this.oldSkuField) {
          this.oldSkuField = this.form.querySelector('input[name="properties[_Old SKU]"]');
        }
        
        // If still not found, try to find it in the entire document
        if (!this.oldSkuField) {
          this.oldSkuField = document.querySelector('#old-sku-prop');
        }
        
        // If still not found, create it
        if (!this.oldSkuField) {
          this.oldSkuField = document.createElement('input');
          this.oldSkuField.type = 'hidden';
          this.oldSkuField.id = 'old-sku-prop';
          this.oldSkuField.name = 'properties[_Old SKU]';
          this.oldSkuField.value = '';
          this.form.appendChild(this.oldSkuField);
        }

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Listen for variant changes to update old SKU field
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.updateOldSkuField(event.data.variant);
        });

        // Initialize old SKU field with current variant
        this.initializeOldSkuField();
        
        // Create a simple mapping for old SKU values
        this.createOldSkuMapping();  
      }

      onSubmitHandler(evt) {
        const linkedTimestamp = this.setTimestampProperty();
        console.log(`[ProductForm] Submitting main product. Generated linkedTimestamp: ${linkedTimestamp}`);
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
            console.log(`[ProductForm] Button is disabled, aborting.`);
            return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        console.log(`[ProductForm] Main product FormData properties:`);
        for (let [key, value] of formData.entries()) {
            if (key.includes('properties')) {
                console.log(`  ${key}: ${value}`);
            }
        }

        config.body = formData;

        window.isAddingPersonalisedItem = true;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
              
              const quantity = formData.get('quantity') || 1;
              // Only add personalisation product after main product is successfully added
              this.addPersonalizationProduct(linkedTimestamp, quantity);
            }
            
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            setTimeout(() => { window.isAddingPersonalisedItem = false; }, 2000);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      updateOldSkuField(variant) {
        if (this.oldSkuField && variant) {
          // First try to get the old SKU from the variant's metafields
          let oldSku = variant.metafields?.custom?.old_sku || '';
          
          // If not available in the variant data, try to get it from our mapping
          if (!oldSku && variant.id && this.oldSkuMapping) {
            const variantData = this.oldSkuMapping[variant.id];
            if (variantData && variantData.old_sku) {
              oldSku = variantData.old_sku;
            }
          }
          
          // If still no old SKU, use the variant SKU as fallback
          if (!oldSku && variant.sku) {
            oldSku = variant.sku;
          }
          
          // If still not available, try to get it from the product data
          if (!oldSku && variant.id) {
            // Get the product handle from the current URL or form data
            const productHandle = this.getProductHandle();
            if (productHandle) {
              this.fetchVariantMetafields(productHandle, variant.id);
            } else {
              // Fallback: try to get from the form's data attributes or other sources
              this.oldSkuField.value = '';
            }
          } else {
            this.oldSkuField.value = oldSku;
          }
        }
      }

      initializeOldSkuField() {
        // Get the current variant from the form
        const currentVariantId = this.variantIdInput.value;
        
        if (currentVariantId && this.oldSkuField) {
          // Try to get the variant data from the page
          const variantDataScript = document.querySelector('variant-selects [data-selected-variant]');
          
          if (variantDataScript) {
            try {
              const variant = JSON.parse(variantDataScript.innerHTML);
              this.updateOldSkuField(variant);
            } catch (error) {
              console.warn('Could not parse variant data:', error);
            }
          }
          
          // Try to get the initial old SKU value from the form
          const initialOldSkuValue = this.form.querySelector('input[name="properties[_Old SKU]"]')?.value;
          if (initialOldSkuValue) {
            this.oldSkuField.value = initialOldSkuValue;
          }
        }
      }

      createOldSkuMapping() {
        this.oldSkuMapping = {};
        
        // Try to get metafield data from preloaded scripts
        const metafieldScripts = document.querySelectorAll('script[data-product-metafields]');
        
        metafieldScripts.forEach(script => {
          try {
            const data = JSON.parse(script.textContent);
            Object.assign(this.oldSkuMapping, data);
          } catch (error) {
            console.warn('Could not parse metafield script:', error);
          }
        });
      }

      getProductHandle() {
        // Try to get product handle from various sources
        let url = window.location.pathname;
        
        // If we're in a modal, try to get the product URL from the modal opener
        const modalOpener = this.closest('quick-add-modal')?.previousElementSibling;
        if (modalOpener && modalOpener.hasAttribute('data-product-url')) {
          const productUrl = modalOpener.getAttribute('data-product-url');
          const match = productUrl.match(/\/products\/([^\/]+)/);
          if (match) return match[1];
        }
        
        // Fallback to current URL
        const match = url.match(/\/products\/([^\/]+)/);
        return match ? match[1] : null;
      }

      async fetchVariantMetafields(productHandle, variantId) {
        try {
          // Try to get metafield data from a preloaded script tag
          const metafieldScript = document.querySelector(`script[data-product-metafields="${productHandle}"]`);
          
          if (metafieldScript) {
            try {
              const metafieldData = JSON.parse(metafieldScript.textContent);
              
              const variantMetafields = metafieldData[variantId];
              
              if (variantMetafields && variantMetafields.old_sku) {
                this.oldSkuField.value = variantMetafields.old_sku;
                return;
              }
            } catch (error) {
              console.warn('Could not parse preloaded metafield data:', error);
            }
          }
          
          // Fallback: try to get from the product page's variant data
          const variantDataScript = document.querySelector('variant-selects [data-selected-variant]');
          
          if (variantDataScript) {
            try {
              const currentVariant = JSON.parse(variantDataScript.innerHTML);
              
              if (currentVariant.id == variantId && currentVariant.metafields?.custom?.old_sku) {
                this.oldSkuField.value = currentVariant.metafields.custom.old_sku;
                return;
              }
            } catch (error) {
              console.warn('Could not parse current variant data:', error);
            }
          }
          
          // Additional fallback: try to get from the initial page load data
          const initialVariantId = this.variantIdInput.value;
          if (initialVariantId == variantId) {
            // This is the initial variant, try to get from the original form value
            const originalOldSkuValue = this.form.querySelector('input[name="properties[_Old SKU]"]')?.value;
            if (originalOldSkuValue) {
              this.oldSkuField.value = originalOldSkuValue;
              return;
            }
          }
          
          this.oldSkuField.value = '';
        } catch (error) {
          console.warn('Could not fetch variant metafields:', error);
          this.oldSkuField.value = '';
        }
      }

      async fetchVariantMetafieldsRest(productHandle, variantId) {
        try {
          const response = await fetch(`/products/${productHandle}.js`);
          if (response.ok) {
            const product = await response.json();
            const variant = product.variants.find(v => v.id === variantId);
            if (variant) {
              // REST API doesn't include metafields, so we'll need to use a different approach
              this.oldSkuField.value = '';
            }
          }
        } catch (error) {
          console.warn('REST API fallback failed:', error);
          this.oldSkuField.value = '';
        }
      }

      disconnectedCallback() {
        // Clean up event listener when component is removed
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

        async addPersonalizationProduct(linkedTimestamp, quantity = 1) {
            let total = 0;

            // Check if the checkbox-controls-required checkbox is ticked
            const controlCheckbox = document.querySelector('.checkbox-controls-required');
            if (controlCheckbox && controlCheckbox.checked) {
                const price = parseFloat(controlCheckbox.getAttribute("data-personalisation-price"));
                if (!isNaN(price)) {
                    total += price;
                }
            } else {
                // Fallback to original logic before PRs - check personalisation-price elements
                const personalisationPrices = document.querySelectorAll('.personalisation-price');
                personalisationPrices.forEach(input => {
                    const price = parseFloat(input.getAttribute("value"));
                    if (!isNaN(price)) {
                        total += price;
                    }
                });
            }

            var personalisationProductName = window.personalisationProductName;
            personalisationProductName = personalisationProductName.toLowerCase();

            if(total > 0){
                console.log(`[ProductForm] Total personalisation cost is > 0, fetching add-on product: ${personalisationProductName}`);
                const response = await fetch(`/products/${personalisationProductName}.js`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || 'Failed to fetch product');
                }

                const product = await response.json();
                const matchingVariant = product.variants.find(variant => (variant.price / 100) === total);

                if (!matchingVariant) {
                    throw new Error('No variant matches the calculated total price');
                }

                console.log(`[ProductForm] Found matching variant for add-on: ${matchingVariant.id}. Submitting...`);
                const formData = new FormData();
                formData.append('id', matchingVariant.id);
                formData.append('quantity', quantity);

                const properties = {
                    'Personalisation ID': linkedTimestamp,
                };
                for (const [key, value] of Object.entries(properties)) {
                    formData.append(`properties[${key}]`, value);
                }

                console.log(`[ProductForm] Add-on product properties:`, properties);

                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                }

                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                config.body = formData;

                window.isAddingPersonalisedItem = true;
                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        this.cart.renderContents(response);
                                    });
                                },
                                { once: true }
                            );
                            quickAddModal.hide(true);
                        } else {
                            try {
                                if (this.cart && typeof this.cart.renderContents === 'function') {
                                    this.cart.renderContents(response);
                                }
                            } catch (error) {
                                console.warn('Cart rendering failed:', error);
                            }
                        }
                    })
                    .catch((e) => {
                        console.error('Error adding personalization product:', e);
                    })
                    .finally(() => {
                        this.setTimestampProperty();
                        setTimeout(() => { window.isAddingPersonalisedItem = false; }, 2000);
                    });
            }
        }

        setTimestampProperty() {
            const timestampInputs = document.querySelectorAll(".personalisation-timestamp");
            const timestamp = Date.now();
            if (timestampInputs.length > 0) {
                timestampInputs.forEach(input => {
                    input.setAttribute("data-timestamp", timestamp);
                    input.setAttribute("value", timestamp);
                    input.value = timestamp;
                });
            }

            return timestamp;
        }

    }
  );
}
